import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api.js';
import { Loading } from '../App.jsx';
import { BrandBar } from '../components/Brand.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import Workspace from '../components/Workspace.jsx';
import { ReportDialog } from '../components/Dialogs.jsx';
import { PlanStore, usePlan } from '../store.js';
import { blankDoc } from '../engine/constants.js';

/** The public face of a share link: the same canvas and inspector, with every
 *  edit path switched off. No session, no history, no other projects. */
export default function Share() {
  const { token } = useParams();
  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = new PlanStore(blankDoc());
  const store = storeRef.current;
  store.shareToken = token;
  usePlan(store);

  const stageRef = useRef(null);
  const controlsRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [themeRev, setThemeRev] = useState(0);
  const [report, setReport] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api.shares.read(token);
        if (!live) return;
        setMeta({ ...r.project, rev: r.rev });
        const doc = { ...blankDoc(), ...r.doc };
        store.setDoc(doc);
        // Workspace owns the cancellable per-floor drawing load and initial
        // fit. A second initial-image callback here could overwrite a newer
        // floor after room navigation (or a changed share token).
      } catch (e) {
        if (live) setError(e.message);
      }
    })();
    return () => { live = false; store.cancelEvaluation(); };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="centred">
        <div className="card">
          <BrandBar />
          <h1>This link is not available</h1>
          <p className="sub">{error} The owner may have revoked it, or it may have expired.</p>
        </div>
      </div>
    );
  }
  if (!meta) return <div className="centred"><Loading what="Opening the shared plan" /></div>;

  return (
    <div className="app">
      <header className="topbar">
        <BrandBar />
        <div className="spacer" />
        <div className="topbar-actions">
          <button className="btn" onClick={() => { if (!store.cov) store.evaluate(); setReport(true); }}>Report</button>
          <ThemeToggle onChange={() => { stageRef.current?.repalette(); setThemeRev(r => r + 1); }} />
        </div>
      </header>

      <div className="readonly-bar">
        <b>{meta.name}</b>
        <span>Shared read-only · revision r{meta.rev}</span>
        <span className="grow" />
        <span>Last updated {new Date(meta.updatedAt + 'Z').toLocaleDateString()}</span>
      </div>

      <Workspace
        store={store}
        readOnly
        stageRef={stageRef}
        controlsRef={controlsRef}
        themeRev={themeRev}
      />

      <ReportDialog open={report} onClose={() => setReport(false)} store={store} projectName={meta.name} revision={meta.rev} />
    </div>
  );
}
